// Grilla de las imágenes ya generadas de la empresa, para elegir CUÁL usar como
// punto de partida. La comparten los modos que arrancan de una imagen: "Animar
// imagen" (→ video) y "Carrusel" (→ variaciones). Se monta inline en desktop
// (debajo del hero, como un historial) y dentro del bottom sheet en mobile — una
// sola implementación para los dos.
//
// Diferencias con GeneratedImagesPicker (el del asistente): selección ÚNICA,
// excluye los videos (de un video no se parte) y devuelve el `id` de la fila, que
// es lo que el backend necesita. Ver ADR-2026-08-14-modo-animar-imagen y
// ADR-2026-08-14-modo-carrusel.

import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import CachedImg from '../../../../components/CachedImg';
import { ProductGridSkeleton } from '../../../../components/ProductGridSkeleton';

const GENERADAS_BUCKET = 'generadas';
const SIGNED_TTL = 3600;
const LIMIT = 60;

// 2 columnas en mobile, 3 de `md` para arriba —
// ADR-2026-08-20-la-grilla-de-elegir-imagen-va-en-dos-columnas-en-mobile.
//
// Eran 3 fijas, con el argumento de que la grilla vive DENTRO de un panel angosto (la
// card de opciones en desktop, el bottom sheet en mobile) y por eso los breakpoints de
// viewport no aplicaban. Vale para no irse a `lg:grid-cols-5` —ahí los tiles quedaban
// minúsculos—, pero no para el corte mobile/desktop: los DOS montajes de esta grilla
// están separados por `md` en los tres modos que la usan (el sheet sólo se abre desde la
// rama mobile, la card inline vive dentro de un `hidden md:flex`). O sea que acá el
// breakpoint de viewport SÍ coincide con el ancho real del contenedor.
//
// En un teléfono, 3 tiles por fila dejaban miniaturas de ~100px: hay que adivinar cuál
// es cuál para elegir. Con 2 se ven.
const GRID_CLASS = 'grid grid-cols-2 gap-3 md:grid-cols-3';

export interface GeneradaPick {
  id: string;
  signedUrl: string;
}

export const GeneradasPickGrid: React.FC<{
  companyId: string | null;
  selectedId: string | null;
  onSelect: (pick: GeneradaPick) => void;
  emptyHint?: string;
}> = ({ companyId, selectedId, onSelect, emptyHint }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<GeneradaPick[]>([]);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from('imagenes_generadas')
        .select('id, file_name, tipo')
        .eq('id_company', companyId)
        .eq('active', true)
        .not('file_name', 'is', null)
        .order('created_at', { ascending: false })
        .limit(LIMIT);
      if (error || !data) {
        if (!cancelled) {
          setRows([]);
          setLoading(false);
        }
        return;
      }
      // Los videos no se animan. El filtro va en JS y no en la query porque
      // `tipo` puede venir null en filas viejas y un `neq` en Postgres las
      // descartaría (NULL <> 'video' es NULL, no true). Mismo criterio de
      // detección que el historial: tipo video/video_pro o file_name .mp4.
      const soloImagenes = data.filter(
        (r: any) =>
          r.tipo !== 'video' &&
          r.tipo !== 'video_pro' &&
          !String(r.file_name).toLowerCase().endsWith('.mp4'),
      );
      const signed = await Promise.all(
        soloImagenes.map(async (r: any) => {
          const { data: s } = await supabase.storage
            .from(GENERADAS_BUCKET)
            .createSignedUrl(`${companyId}/full/${r.file_name}`, SIGNED_TTL);
          if (!s?.signedUrl) return null;
          return { id: String(r.id), signedUrl: s.signedUrl } as GeneradaPick;
        }),
      );
      if (!cancelled) {
        setRows(signed.filter((x): x is GeneradaPick => !!x));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  if (loading) {
    return (
      <ProductGridSkeleton
        count={10}
        gridClassName={GRID_CLASS}
        tileClassName="rounded-[13px]"
      />
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center text-[14px] text-[#3C3C43]/55 py-12 px-6">
        {emptyHint ??
          'Todavía no generaste ninguna imagen. Subí una foto para animarla.'}
      </div>
    );
  }

  return (
    <div className={GRID_CLASS}>
      {rows.map((r) => {
        const isSel = selectedId === r.id;
        return (
          <button
            key={r.id}
            type="button"
            onClick={() => onSelect(r)}
            className={`relative aspect-square rounded-[13px] overflow-hidden bg-neutral-100 transition-all active:scale-[0.98] ${
              isSel
                ? 'ring-2 ring-brand-primary'
                : 'ring-1 ring-black/[0.06] hover:ring-brand-primary/40'
            }`}
          >
            <CachedImg
              src={r.signedUrl}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover"
            />
            {isSel && (
              <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary text-white shadow-sm">
                <Check className="w-3.5 h-3.5" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default GeneradasPickGrid;
